import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Card,
  CardContent,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Badge,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import {
  Sports as SportsIcon,
  AccessTime as TimeIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
  Public as PublicIcon
} from '@mui/icons-material';
import { ChatService } from '../../services/chatService';
import { useAuth } from '../../contexts/AuthContext';
import GameChatDialog from '../chat/GameChatDialog';
import { SportIcon } from '../../utils/sportIcons';
import BackButton from '../BackButton';

const ChatScreen = () => {
  const { currentUser } = useAuth();
  const theme = useTheme();
  // Chat accent tokens (see chatColorsFor in App.js): keep the
  // sport chips, header icon and tab indicator legible in both
  // modes — outlined 'primary' is dark green on dark green in
  // dark mode, and the secondary header icon washes out in light.
  const chat = theme.palette.chat;
  const [activeChannels, setActiveChannels] = useState([]);
  const [userChats, setUserChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    let unsubscribeActive = () => {};
    let unsubscribeUser = () => {};

    if (currentUser) {
      // Subscribe to active channels (public view)
      unsubscribeActive = ChatService.subscribeToActiveChannels(
        (channels) => {
          setActiveChannels(channels);
          if (currentTab === 1) setLoading(false);
        }
      );

      // Subscribe to user's personal chats
      unsubscribeUser = ChatService.subscribeToUserChats(
        currentUser.uid,
        (channels) => {
          setUserChats(channels);
          if (currentTab === 0) setLoading(false);
        }
      );
    } else {
      setLoading(false);
    }

    return () => {
      unsubscribeActive();
      unsubscribeUser();
    };
  }, [currentUser, currentTab]);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setLoading(true);
  };

  const handleChannelClick = async (channel) => {
    // Parse the game ID to get the original sport_key
    const parsedId = ChatService.parseGameId(channel.gameId);
    
    // Determine team names with fallback logic
    let homeTeam = channel.homeTeam || 'Unknown';
    let awayTeam = channel.awayTeam || 'Unknown';
    
    // If we don't have team names and it's the new format, try to extract from parsedId
    if ((homeTeam === 'Unknown' || awayTeam === 'Unknown') && 
        parsedId.format === 'new' && parsedId.teams.length === 2) {
      [awayTeam, homeTeam] = parsedId.teams;
    }
    
    // Convert channel data back to game format for the dialog
    const gameData = {
      id: channel.gameId,
      home_team: homeTeam,
      away_team: awayTeam,
      sport_title: channel.sport,
      sport_key: parsedId.sport_key,
      commence_time: channel.commenceTime
    };
    
    setSelectedChannel(gameData);
    setChatDialogOpen(true);

    // Mark as read if this is from user's personal chats
    if (currentTab === 0 && currentUser && channel.userInfo?.unreadCount > 0) {
      try {
        await ChatService.markAsRead(channel.gameId, currentUser.uid);
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedChannel(null);
  };

  const formatLastActivity = (timestamp) => {
    if (!timestamp) return 'No activity';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Active now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const renderChannelList = (channels, isUserChats = false) => {
    if (channels.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <SportsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {isUserChats ? 'No Chat History' : 'No Active Game Chats'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isUserChats 
                ? 'Start chatting about games to see your conversation history here.'
                : 'Game chats will appear here when users start conversations about specific games.'
              }
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card>
        <CardContent sx={{ p: 0 }}>
          <List>
            {channels.map((channel, index) => (
              <React.Fragment key={channel.id}>
                <ListItem disablePadding>
                  <ListItemButton 
                    onClick={() => handleChannelClick(channel)}
                    sx={{ py: 2 }}
                  >
                    <ListItemAvatar>
                      <Badge 
                        badgeContent={isUserChats ? channel.userInfo?.unreadCount || 0 : 0}
                        color="error"
                        invisible={!isUserChats || !channel.userInfo?.unreadCount}
                      >
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <SportIcon sportTitle={channel.sport} fontSize="small" />
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {channel.homeTeam} vs {channel.awayTeam}
                          </Typography>
                          <Chip 
                            icon={<SportIcon sportTitle={channel.sport} fontSize="small" />}
                            label={channel.sport} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ color: chat.chipAccent, borderColor: chat.chipAccent }} 
                          />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            <TimeIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="text.secondary">
                              {new Date(channel.commenceTime).toLocaleString()}
                            </Typography>
                          </Box>
                          
                          {/* Show last message for user chats */}
                          {isUserChats && channel.userInfo?.lastMessage && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                <strong>{channel.userInfo.lastMessage.senderName}:</strong>{' '}
                                {channel.userInfo.lastMessage.text}
                              </Typography>
                            </Box>
                          )}
                          
                          <Box display="flex" alignItems="center" gap={2} mt={0.5}>
                            <Typography variant="body2" color="text.secondary">
                              {channel.messageCount || 0} messages
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {isUserChats && channel.userInfo?.lastSeen 
                                ? `Last seen: ${formatLastActivity(channel.userInfo.lastSeen)}`
                                : `Last activity: ${formatLastActivity(channel.lastActivity)}`
                              }
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < channels.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!currentUser) {
    return (
      <Box sx={{ py: 2 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <ChatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Please Sign In
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to view your chat history and join game discussions.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      <BackButton />
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <ChatIcon sx={{ fontSize: 48, color: chat.headerIcon, mb: 1 }} />
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Game Chats
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Join conversations about live games and upcoming matches
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          sx={{ '& .MuiTabs-indicator': { backgroundColor: chat.tabIndicator } }}
        >
          <Tab 
            icon={<PersonIcon />} 
            label="My Chats" 
            iconPosition="start"
          />
          <Tab 
            icon={<PublicIcon />} 
            label="All Active" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && renderChannelList(userChats, true)}
      {currentTab === 1 && renderChannelList(activeChannels, false)}

      {/* Game Chat Dialog */}
      <GameChatDialog
        open={chatDialogOpen}
        onClose={handleCloseChatDialog}
        game={selectedChannel}
      />
    </Box>
  );
};

export default ChatScreen;
